import React, { useState, useMemo, useCallback } from "react";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import BackBtn from "../../widgets/BackBtn";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import {
  Alert,
  Box,
  Container,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

const VoiceClientsLeads = () => {
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/voice/get-voice-leads-status`
  );
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(
        `${API_ENDPOINT}/root/refresh-voice-leads-stats`,
        getHeader()
      );
      refetch();
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Filter and sort the data
  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];

    // First, filter by search term
    const filtered = data.filter((client) =>
      client._id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Then, sort in descending order
    return filtered.sort((a, b) => {
      // Sort by Fully Qualified Leads (Descending)
      if (b.totalFullyQualifiedLeads !== a.totalFullyQualifiedLeads) {
        return b.totalFullyQualifiedLeads - a.totalFullyQualifiedLeads;
      }
      // If Fully Qualified Leads are equal, sort by Partial Leads (Descending)
      return b.totalPartialLeads - a.totalPartialLeads;
    });
  }, [data, searchTerm]);

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container sx={{ mt: 3, mb: 5 }}>
      <BackBtn />

      <Box sx={{ mt: 3, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6">Voice Client Lead Stats</Typography>

          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <TextField
          fullWidth
          label="Search by Client ID"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="e.g. Ramesh Chennai"
        />
      </Box>
      <TableContainer component={Paper} elevation={3}>
        <Table sx={{ minWidth: 650 }} aria-label="client leads table">
          <TableHead sx={{ backgroundColor: "primary.main" }}>
            <TableRow>
              <TableCell
                sx={{ color: "primary.contrastText", fontWeight: "bold" }}
              >
                Client ID
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "primary.contrastText", fontWeight: "bold" }}
              >
                Total Answered
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "primary.contrastText", fontWeight: "bold" }}
              >
                Partial Leads
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "primary.contrastText", fontWeight: "bold" }}
              >
                Qualified Leads
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "primary.contrastText", fontWeight: "bold" }}
              >
                Credits Used
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "primary.contrastText", fontWeight: "bold" }}
              >
                Last Updated
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedData.length > 0 ? (
              filteredAndSortedData.map((row) => (
                <TableRow
                  key={row._id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 500 }}
                  >
                    {row._id}
                  </TableCell>
                  <TableCell align="right">
                    {row.totalCalls.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {row.totalPartialLeads.toLocaleString()}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "success.main", fontWeight: "bold" }}
                  >
                    {row.totalFullyQualifiedLeads.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {row.totalCreditsUsed.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {new Date(row.lastUpdated).toLocaleDateString("en-IN", {
                      timeZone: "Asia/Kolkata",
                    })}{" "}
                    {new Date(row.lastUpdated).toLocaleTimeString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No clients found matching "{searchTerm}"
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default VoiceClientsLeads;
